import "~/app.css";

import type { Route } from "./+types/root";

import { useEffect } from "react";
import { ORPCError } from "@orpc/client";
import { PostHogProvider } from "@posthog/react";
import * as Sentry from "@sentry/react";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { formDevtoolsPlugin } from "@tanstack/react-form-devtools";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { lightTheme } from "amazon-chime-sdk-component-library-react";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import {
  href,
  isRouteErrorResponse,
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "react-router";
import { ThemeProvider } from "styled-components";

import { isPaladinApiError } from "@paladin/errors";
import { i18n, initI18n, useTranslation } from "@paladin/i18n";

import { PaladinToastContainer } from "~/pld/components/toast/paladin-toast-container";
import { apiErrorBody } from "~/pld/lib/api-error-body";
import { getQueryClient } from "~/pld/lib/get-query-client";
import { getPostHogClient } from "~/pld/lib/posthog";
import { auth0AuthProvider } from "~/pld/services/auth0";
import { socket } from "~/pld/services/event-websocket";
import Button, { buttonVariants } from "~/pld/ui/button";
import Loader from "~/pld/ui/loader";

// v11 collects bodies, cookies, and query payloads unless this is set.
// Keep the v10 baseline: the app handles patient data.
const sentryDataCollection = {
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
  },
  httpBodies: [],
  urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  graphQL: { document: false, variables: false },
  queues: false,
};

// Initialize Sentry only when DSN is provided
if (typeof import.meta.env.VITE_SENTRY_DSN === "string") {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    attachStacktrace: true,
    dataCollection: sentryDataCollection,
    integrations: [
      Sentry.browserTracingIntegration(),
      // RGPD compliance: Mask sensitive patient data and personal information in session replays
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Catch 100% of sessions with error
    replaysOnErrorSampleRate: 1.0,
  });
}

void initI18n();

export const links: Route.LinksFunction = () => [
  { rel: "icon", href: "/icons/brand-logo-symbol.png" },
];

export const meta: Route.MetaFunction = () => [{ title: "Paladin" }];

const Fonts = () => (
  <>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link
      rel="preconnect"
      href="https://fonts.gstatic.com"
      crossOrigin="anonymous"
    />
    <link
      href="https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Syne:wght@400..800&display=swap"
      rel="stylesheet"
    />
  </>
);

function Yousign() {
  return (
    <script
      src="https://cdn.yousign.tech/iframe-sdk-1.6.0.min.js"
      integrity="sha384-/7MD1voOOzWVz7FmgeMwmmd1DO85Mo0PkkxdYd9j2wDGzGDGRG/phgnL0c9Xyy52"
      crossOrigin="anonymous"
      async
    ></script>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const queryClient = getQueryClient();
  const locale = i18n.language;

  return (
    // Google translate causes React to crash. See: https://github.com/facebook/react/issues/11538#issuecomment-3686702663
    <html lang={locale} translate="no">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Required for Yousign iFrame SDK */}
        <meta name="referrer" content="strict-origin-when-cross-origin" />
        {/* Google translate causes React to crash. See: https://github.com/facebook/react/issues/11538#issuecomment-3686702663 */}
        <meta name="google" content="notranslate" />
        <Meta />
        <Links />
        <Fonts />
        <Yousign />
      </head>

      {/* Google translate causes React to crash. See: https://github.com/facebook/react/issues/11538#issuecomment-3686702663 */}
      <body className="notranslate">
        <QueryClientProvider client={queryClient}>
          <div className="bg-background">{children}</div>
        </QueryClientProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const clientLoader = async () => {
  const isAuthenticated = await auth0AuthProvider.isAuthenticated();
  return { isAuthenticated };
};

export function HydrateFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Loader />
    </div>
  );
}

export default function App() {
  const loaderData = useLoaderData<typeof clientLoader>();

  useEffect(() => {
    if (loaderData.isAuthenticated) {
      const connectToWebsocket = async () => {
        const token = await auth0AuthProvider.getTokenSilently();
        socket.auth = { "x-auth-token": token };
        socket.connect();
      };

      void connectToWebsocket();
    }
  }, [loaderData.isAuthenticated]);

  return (
    <>
      <PostHogProvider client={getPostHogClient()}>
        {/* This theme is only needed by chime SDK */}
        <ThemeProvider theme={lightTheme}>
          <NuqsAdapter>
            <Outlet />
          </NuqsAdapter>
        </ThemeProvider>
        <PaladinToastContainer />
      </PostHogProvider>
      <TanStackDevtools
        plugins={[
          {
            name: "TanStack Query",
            render: <ReactQueryDevtoolsPanel />,
            defaultOpen: true,
          },
          formDevtoolsPlugin(),
        ]}
      />
    </>
  );
}

const AuthButNoValidAccountErrorComponent = () => {
  const { t } = useTranslation(["errors", "common"]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      void auth0AuthProvider.signout();
    }, 5000);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <div>
      <div>{t("errors:noAccountAssociatedToThisSSOUser")}</div>
      <div>{t("common:youAreGoingToGetLoggedOut")}</div>
      <Button onClick={() => auth0AuthProvider.signout()}>
        {t("common:logMeOutNow")}
      </Button>
    </div>
  );
};

const AccessDeniedErrorComponent = () => {
  const { t } = useTranslation(["errors"]);

  return (
    <main className="container mx-auto flex flex-col items-start gap-4 p-4 pt-16">
      <h1>{t("errors:accessDenied.title")}</h1>
      <p>{t("errors:accessDenied.description")}</p>
      <Link
        to={href("/")}
        className={buttonVariants({ variant: "primary" })}
        data-attr="access-denied-back-home"
      >
        {t("errors:accessDenied.backHome")}
      </Link>
    </main>
  );
};

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (
    isPaladinApiError(
      apiErrorBody(error),
      "NoAccountAssociatedToThisSsoUserError",
    )
  ) {
    return <AuthButNoValidAccountErrorComponent />;
  }

  // A refusal is an answer, not a crash: say so rather than "Oops!".
  const isAccessDenied = error instanceof ORPCError && error.status === 403;

  if (isAccessDenied) {
    return <AccessDeniedErrorComponent />;
  }

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="container mx-auto p-4 pt-16">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full overflow-x-auto p-4">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
